import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { persist } from 'zustand/middleware';
import { useCallback } from 'react';

// ---------------------------------------------------
// Local storage
// ---------------------------------------------------
export const COMMON = 'COMMON';
export type PapersSidebarState = 'open' | 'closed';
export type SentenceInfo = {
  paperId: string;
  sentence: string;
} | null;

type LocalState = {
  papersGridMode: Record<string, number>;
  setPapersGridMode: (subjectId: string, mode: number) => void;
  getPapersGridMode: (subjectId: string) => number;
  papersViewerSidebarState: PapersSidebarState;
  setPapersViewerSidebarState: (sidebarState: PapersSidebarState) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  // AI 列 sidbar
  aiColSidebarCollapsedMap: Record<string, boolean>;
  setAiColSidebarCollapsed: (subjectId: string, collapsed: boolean) => void;
  getAiColSidebarCollapsed: (subjectId: string) => boolean;
  traceSentenceInfo: SentenceInfo;
  setTraceSentenceInfo: (origin: SentenceInfo) => void;
  renewalSonnerOpen: boolean;
  renewalSonnerOpenTime: number;
  resetRenewalSonnerOpenTime: () => void;
  setRenewalSonnerOpen: (open: boolean) => void;
  clear: () => void;
}
export const useLocal = create<
  LocalState,
  [
    ["zustand/immer", never],
    ['zustand/persist', LocalState],
  ]
>(immer(persist((set, get) => ({
  papersGridMode: {},
  setPapersGridMode: (subjectId, mode) => {
    set((state) => {
      state.papersGridMode[subjectId || 'ALL_PAPERS'] = mode;
    });
  },
  getPapersGridMode: (subjectId) => {
    return get().papersGridMode[subjectId || 'ALL_PAPERS'] || 0;
  },
  papersViewerSidebarState: 'open',
  setPapersViewerSidebarState: (sidebarState) => {
    set((state) => {
      state.papersViewerSidebarState = sidebarState;
    });
  },
  sidebarCollapsed: false,
  setSidebarCollapsed: (collapsed) => {
    set((state) => {
      state.sidebarCollapsed = collapsed;
    });
  },
  aiColSidebarCollapsedMap: {},
  setAiColSidebarCollapsed: (subjectId, collapsed) => {
    const id = subjectId || COMMON;
    set((state) => {
      state.aiColSidebarCollapsedMap[id] = collapsed;
    });
  },
  getAiColSidebarCollapsed: (subjectId) => {
    const id = subjectId || COMMON;
    return get().aiColSidebarCollapsedMap[id] ?? false;
  },
  traceSentenceInfo: null,
  setTraceSentenceInfo: (origin: SentenceInfo) => {
    set((state) => {
      state.traceSentenceInfo = origin;
    });
  },
  renewalSonnerOpen: false,
  renewalSonnerOpenTime: 0,
  resetRenewalSonnerOpenTime() {
    set((state) => {
      state.renewalSonnerOpenTime = 0;
    });
  },
  setRenewalSonnerOpen: (open) => {
    set((state) => {
      // 最多打开一次，防止重复打开
      if (open === true && state.renewalSonnerOpenTime > 0) {
        return;
      }
      state.renewalSonnerOpen = open;
      state.renewalSonnerOpenTime += 1;
    });
  },
  clear: () => {
    set((state) => {
      state.papersGridMode = {};
      state.papersViewerSidebarState = 'open';
      state.sidebarCollapsed = false;
      state.aiColSidebarCollapsedMap = {};
      state.traceSentenceInfo = null;
      state.renewalSonnerOpen = false;
      state.renewalSonnerOpenTime = 0;
    });
  },
}), {
  name: 'findin-storage',
})));

export const usePapersGridMode = () => useLocal(state => ({
  getPapersGridMode: state.getPapersGridMode,
  setPapersGridMode: state.setPapersGridMode,
}));

export const usePapersViewerSidebarState = () => useLocal(state => ({
  sidebarState: state.papersViewerSidebarState,
  setSidebarState: state.setPapersViewerSidebarState,
}));

// 主 sidebar
export const useSidebarCollapsed = () => useLocal(state => ({
  collapsed: state.sidebarCollapsed,
  setCollapsed: state.setSidebarCollapsed,
}));

// AI 列的菜单收起
export const useAiColSidebarCollapsed = (subjectId: string) => {
  const getAiColSidebarCollapsed = useLocal(state => state.getAiColSidebarCollapsed);
  const setAiColSidebarCollapsed = useLocal(state => state.setAiColSidebarCollapsed);

  const collapsed = getAiColSidebarCollapsed(subjectId);

  const setCollapsed = useCallback((collapsed: boolean) => (
    setAiColSidebarCollapsed(subjectId, collapsed)
  ), [setAiColSidebarCollapsed, subjectId]);

  return {
    collapsed,
    setCollapsed,
  }
};

export const useTraceSentenceOrigin = () => useLocal(state => ({
  traceSentenceInfo: state.traceSentenceInfo,
  setTraceSentenceInfo: state.setTraceSentenceInfo,
}));

export const useRenewalSonnerOpen = () => useLocal(state => ({
  open: state.renewalSonnerOpen,
  setOpen: state.setRenewalSonnerOpen,
  resetOpenTime: state.resetRenewalSonnerOpenTime,
}));

// 清除所有
export const useLocalClear = () => useLocal(state => ({
  clear: state.clear,
}));
